// Curated Scripture pool for Selah Downloads.
//
// Goals:
//   1. Give the app a richer, less repetitive spiritual atmosphere —
//      users stop seeing the same three verses every session.
//   2. Group verses by mood so pages can request context-appropriate
//      Scripture (e.g. "rest" on Welcome, "guidance" on Dashboard).
//   3. Rotate deterministically per-page-key using localStorage so the
//      verse changes across visits, but doesn't flicker on re-renders.
//
// Verses are ESV translation for consistency with the AI analyzer.
// All references verified.

export const SCRIPTURES = [
  // —— Rest / Stillness
  {
    text: 'Be still, and know that I am God.',
    reference: 'Psalm 46:10',
    translation: 'ESV',
    mood: 'rest',
  },
  {
    text: 'Come to me, all who labor and are heavy laden, and I will give you rest.',
    reference: 'Matthew 11:28',
    translation: 'ESV',
    mood: 'rest',
  },
  {
    text: 'In returning and rest you shall be saved; in quietness and in trust shall be your strength.',
    reference: 'Isaiah 30:15',
    translation: 'ESV',
    mood: 'rest',
  },
  {
    text: 'For God alone, O my soul, wait in silence, for my hope is from him.',
    reference: 'Psalm 62:5',
    translation: 'ESV',
    mood: 'rest',
  },
  {
    text: 'He makes me lie down in green pastures. He leads me beside still waters. He restores my soul.',
    reference: 'Psalm 23:2–3',
    translation: 'ESV',
    mood: 'rest',
  },

  // —— Guidance / Illumination
  {
    text: 'Your word is a lamp to my feet and a light to my path.',
    reference: 'Psalm 119:105',
    translation: 'ESV',
    mood: 'guidance',
  },
  {
    text: 'Trust in the LORD with all your heart, and do not lean on your own understanding.',
    reference: 'Proverbs 3:5',
    translation: 'ESV',
    mood: 'guidance',
  },
  {
    text: 'In all your ways acknowledge him, and he will make straight your paths.',
    reference: 'Proverbs 3:6',
    translation: 'ESV',
    mood: 'guidance',
  },
  {
    text: 'And your ears shall hear a word behind you, saying, “This is the way, walk in it.”',
    reference: 'Isaiah 30:21',
    translation: 'ESV',
    mood: 'guidance',
  },
  {
    text: 'When the Spirit of truth comes, he will guide you into all the truth.',
    reference: 'John 16:13',
    translation: 'ESV',
    mood: 'guidance',
  },
  {
    text: 'The unfolding of your words gives light; it imparts understanding to the simple.',
    reference: 'Psalm 119:130',
    translation: 'ESV',
    mood: 'guidance',
  },

  // —— Peace
  {
    text: 'You keep him in perfect peace whose mind is stayed on you, because he trusts in you.',
    reference: 'Isaiah 26:3',
    translation: 'ESV',
    mood: 'peace',
  },
  {
    text: 'Peace I leave with you; my peace I give to you. Let not your hearts be troubled, neither let them be afraid.',
    reference: 'John 14:27',
    translation: 'ESV',
    mood: 'peace',
  },
  {
    text: 'Do not be anxious about anything, but in everything by prayer and supplication with thanksgiving let your requests be made known to God.',
    reference: 'Philippians 4:6',
    translation: 'ESV',
    mood: 'peace',
  },

  // —— Courage / Strength
  {
    text: 'Be strong and courageous. Do not be frightened, for the LORD your God is with you wherever you go.',
    reference: 'Joshua 1:9',
    translation: 'ESV',
    mood: 'courage',
  },
  {
    text: 'Fear not, for I am with you; be not dismayed, for I am your God; I will strengthen you.',
    reference: 'Isaiah 41:10',
    translation: 'ESV',
    mood: 'courage',
  },
  {
    text: 'For God gave us a spirit not of fear but of power and love and self-control.',
    reference: '2 Timothy 1:7',
    translation: 'ESV',
    mood: 'courage',
  },

  // —— Purpose / Calling
  {
    text: 'For I know the plans I have for you, declares the LORD, plans for welfare and not for evil, to give you a future and a hope.',
    reference: 'Jeremiah 29:11',
    translation: 'ESV',
    mood: 'purpose',
  },
  {
    text: 'For we are his workmanship, created in Christ Jesus for good works, which God prepared beforehand, that we should walk in them.',
    reference: 'Ephesians 2:10',
    translation: 'ESV',
    mood: 'purpose',
  },
  {
    text: 'He who began a good work in you will bring it to completion at the day of Jesus Christ.',
    reference: 'Philippians 1:6',
    translation: 'ESV',
    mood: 'purpose',
  },
  {
    text: 'And we know that for those who love God all things work together for good, for those who are called according to his purpose.',
    reference: 'Romans 8:28',
    translation: 'ESV',
    mood: 'purpose',
  },

  // —— Wisdom
  {
    text: 'If any of you lacks wisdom, let him ask God, who gives generously to all without reproach.',
    reference: 'James 1:5',
    translation: 'ESV',
    mood: 'wisdom',
  },
  {
    text: 'For the LORD gives wisdom; from his mouth come knowledge and understanding.',
    reference: 'Proverbs 2:6',
    translation: 'ESV',
    mood: 'wisdom',
  },
  {
    text: 'The fear of the LORD is the beginning of wisdom, and the knowledge of the Holy One is insight.',
    reference: 'Proverbs 9:10',
    translation: 'ESV',
    mood: 'wisdom',
  },

  // —— Provision / Presence
  {
    text: 'The LORD is my shepherd; I shall not want.',
    reference: 'Psalm 23:1',
    translation: 'ESV',
    mood: 'provision',
  },
  {
    text: 'Delight yourself in the LORD, and he will give you the desires of your heart.',
    reference: 'Psalm 37:4',
    translation: 'ESV',
    mood: 'provision',
  },
  {
    text: 'Behold, I am with you always, to the end of the age.',
    reference: 'Matthew 28:20',
    translation: 'ESV',
    mood: 'provision',
  },
];

const MOODS = new Set(SCRIPTURES.map((s) => s.mood));

const pickByMood = (mood) =>
  mood && MOODS.has(mood)
    ? SCRIPTURES.filter((s) => s.mood === mood)
    : SCRIPTURES;

/**
 * Returns a Scripture selected by cycling through the pool for a given key,
 * so revisits show different verses instead of repeating the same one.
 *
 * @param {string} key   — unique per surface (e.g. 'welcome-hero', 'dashboard').
 * @param {string} mood  — optional mood filter ('rest', 'guidance', etc.).
 * @param {number} seed  — optional numeric offset (used by <RotatingScripture />
 *                          to force a fresh pick on remount without touching localStorage).
 */
export const getRotatingScripture = (key = 'default', mood = null, seed = 0) => {
  const pool = pickByMood(mood);
  if (pool.length === 0) return SCRIPTURES[0];

  const storageKey = `sd_scripture_idx__${key}`;
  let idx = 0;
  try {
    const stored = parseInt(localStorage.getItem(storageKey) || '-1', 10);
    idx = ((Number.isFinite(stored) ? stored : -1) + 1 + seed) % pool.length;
    localStorage.setItem(storageKey, String(idx));
  } catch (_) {
    idx = Math.floor(Math.random() * pool.length);
  }
  return pool[idx];
};

/**
 * Deterministic pick that does NOT touch localStorage — useful when you
 * want stable Scripture per session but different across sessions.
 */
export const pickScriptureForSession = (mood = null) => {
  const pool = pickByMood(mood);
  return pool[Math.floor(Math.random() * pool.length)];
};
